import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import ProductCard from '../components/ProductCard';
import api from '../lib/api';
import { ArrowRight } from 'lucide-react';

// Standard horizontal banner
const HorizontalBanner = ({ tile, height = 'h-[220px]' }) => {
  if (!tile || !tile.visible) return null;
  const imgLeft = tile.image_position === 'left';
  return (
    <Link to={tile.link_url || '/noleggio'} className={`banner-card block ${height}`} style={{ backgroundColor: tile.bg_color }}>
      <div className="relative w-full h-full flex items-center px-6 md:px-8 overflow-hidden">
        {imgLeft && tile.image_url && (
          <div className="absolute left-2 top-1/2 -translate-y-1/2 h-[80%] w-[35%] flex items-center justify-center">
            <img src={tile.image_url} alt={tile.title} className="max-h-full max-w-full w-auto h-auto object-contain" loading="lazy" />
          </div>
        )}
        <div className={`relative z-10 ${imgLeft ? 'ml-[40%]' : 'mr-[40%]'}`} style={{ color: tile.text_color }}>
          {tile.badge && <span className="inline-block mb-2 text-xs font-bold uppercase tracking-wider opacity-80">{tile.badge}</span>}
          <h3 className="text-2xl md:text-3xl font-black brand-text leading-tight">{tile.title}</h3>
          {tile.subtitle && <p className="mt-1 text-sm md:text-base opacity-90">{tile.subtitle}</p>}
        </div>
        {!imgLeft && tile.image_url && (
          <div className="absolute right-2 top-1/2 -translate-y-1/2 h-[80%] w-[35%] flex items-center justify-center">
            <img src={tile.image_url} alt={tile.title} className="max-h-full max-w-full w-auto h-auto object-contain" loading="lazy" />
          </div>
        )}
      </div>
    </Link>
  );
};

const VerticalCard = ({ tile, height = 'h-[280px]' }) => {
  if (!tile || !tile.visible) return null;
  return (
    <Link to={tile.link_url || '/noleggio'} className={`banner-card relative ${height} rounded-lg overflow-hidden p-6 flex flex-col`}
      style={{ backgroundColor: tile.bg_color, color: tile.text_color }}>
      {tile.badge && <span className="text-2xl font-black brand-text" style={{ color: tile.text_color }}>{tile.badge}</span>}
      <h3 className="mt-2 text-2xl font-bold brand-text" style={{ color: tile.text_color }}>{tile.title}</h3>
      {tile.subtitle && <p className="mt-1 text-sm font-bold opacity-90" style={{ color: tile.text_color }}>{tile.subtitle}</p>}
      {tile.image_url && (
        <div className="mt-auto flex items-end justify-center w-full overflow-hidden" style={{ maxHeight: '60%' }}>
          <img src={tile.image_url} alt="" className="max-h-full max-w-full w-auto h-auto object-contain" loading="lazy" />
        </div>
      )}
    </Link>
  );
};

const Home = () => {
  const [categories, setCategories] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [tiles, setTiles] = useState({});

  useEffect(() => {
    api.get('/categories').then((r) => setCategories(r.data)).catch(() => {});
    api.get('/products?featured=true').then((r) => setFeatured(r.data.slice(0, 8))).catch(() => {});
    api.get('/home-tiles').then((r) => {
      const map = {};
      r.data.forEach((t) => { map[t.slot] = t; });
      setTiles(map);
    }).catch(() => {});
  }, []);

  // Top-level categories only for the home grid
  const topCategories = useMemo(() => categories.filter((c) => !c.parent_id), [categories]);
  // Subcategories grouped per top category
  const subCount = (parentId) => categories.filter((c) => c.parent_id === parentId).length;

  const t = (n) => tiles[n];

  return (
    <div className="max-w-7xl mx-auto px-4 lg:px-6 py-8 fade-in">
      {/* Section 1: All Categories grid */}
      {topCategories.length > 0 && (
        <section>
          <div className="flex items-end justify-between mb-6">
            <div>
              <h2 className="text-3xl font-black brand-text text-neutral-900">Categorie</h2>
              <p className="text-neutral-600 mt-1">Esplora tutte le categorie del nostro catalogo</p>
            </div>
            <Link to="/noleggio" className="hidden sm:inline-flex items-center gap-1 text-sm font-bold text-[#d62828] hover:gap-2 transition-all">
              Vedi tutto <ArrowRight size={16} />
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {topCategories.map((c) => (
              <Link
                key={c.id}
                to={`/categoria/${c.slug}`}
                className="group relative aspect-[4/3] rounded-lg overflow-hidden border border-neutral-200 hover:border-[#d62828]/50 hover:shadow-lg transition-all"
              >
                {c.image_url ? (
                  <img src={c.image_url} alt={c.name} className="absolute inset-0 w-full h-full object-contain p-4 group-hover:scale-105 transition-transform duration-500" />
                ) : (
                  <div className="absolute inset-0 bg-gradient-to-br from-neutral-200 to-neutral-300" />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
                  <h3 className="font-black text-xl brand-text">{c.name}</h3>
                  {subCount(c.id) > 0 && (
                    <p className="text-xs opacity-90 mt-0.5">{subCount(c.id)} sottocategorie</p>
                  )}
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Section 2: Featured products */}
      {featured.length > 0 && (
        <section className="mt-14">
          <div className="flex items-end justify-between mb-6">
            <div>
              <h2 className="text-3xl font-black brand-text text-neutral-900">In Evidenza</h2>
              <p className="text-neutral-600 mt-1">I prodotti più richiesti dai professionisti</p>
            </div>
            <Link to="/noleggio" className="hidden sm:inline-flex items-center gap-1 text-sm font-bold text-[#d62828] hover:gap-2 transition-all">
              Vedi tutti <ArrowRight size={16} />
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {featured.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        </section>
      )}

      {/* Section 3: 14 customizable tiles */}
      <section className="mt-14">
        <h2 className="text-3xl font-black brand-text text-neutral-900 mb-6">Promozioni e Highlight</h2>

        {/* Slot 1-2 */}
        <div className="grid md:grid-cols-2 gap-5">
          <HorizontalBanner tile={t(1)} />
          <HorizontalBanner tile={t(2)} />
        </div>

        {/* Slot 3-4 */}
        <div className="grid md:grid-cols-2 gap-5 mt-5">
          {t(3) && t(3).visible && (
            <Link to={t(3).link_url} className="banner-card flex items-stretch overflow-hidden h-[140px]" style={{ backgroundColor: t(3).bg_color }}>
              <div className="bg-white px-6 flex items-center w-1/3 rounded-l-md">
                <span className="font-black brand-text text-xl text-neutral-900">{t(3).badge || 'Betti|store'}</span>
              </div>
              <div className="flex-1 flex flex-col justify-center px-6" style={{ color: t(3).text_color }}>
                <p className="text-lg font-bold brand-text">{t(3).title}</p>
                <p className="text-sm opacity-90">{t(3).subtitle}</p>
              </div>
            </Link>
          )}
          {t(4) && t(4).visible && (
            <Link to={t(4).link_url} className="banner-card flex items-stretch overflow-hidden h-[140px]" style={{ backgroundColor: t(4).bg_color }}>
              <div className="flex-1 flex flex-col justify-center px-6" style={{ color: t(4).text_color }}>
                <p className="text-lg font-bold brand-text">{t(4).title}</p>
                <p className="text-sm opacity-90">{t(4).subtitle}</p>
                {t(4).badge && <span className="inline-block mt-2 px-3 py-1 bg-white text-[#d62828] text-xs font-bold rounded w-max">{t(4).badge}</span>}
              </div>
              <div className="bg-white px-6 flex items-center w-1/3 rounded-r-md justify-end">
                <span className="font-black brand-text text-lg text-neutral-900 text-right leading-tight">
                  Betti<span className="text-[#d62828]">SRL</span>
                  <br />
                  <span className="text-[10px] uppercase tracking-wider text-neutral-500">Audio · Lights · Video</span>
                </span>
              </div>
            </Link>
          )}
        </div>

        {/* Slot 5: Sound Devices */}
        {t(5) && t(5).visible && (
          <div className="mt-5">
            <Link to={t(5).link_url} className="banner-card block h-[200px] rounded-lg overflow-hidden relative" style={{ backgroundColor: t(5).bg_color }}>
              <div className="absolute left-0 top-0 bottom-0 w-1/3 flex items-center justify-center">
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-44 h-44 rounded-full bg-[#f08019] opacity-90" />
                </div>
                {t(5).image_url && <img src={t(5).image_url} alt="" className="relative max-h-32 max-w-[70%] w-auto object-contain z-10" />}
              </div>
              <div className="absolute right-0 top-0 bottom-0 w-1/3 flex items-center justify-center">
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-44 h-44 rounded-full bg-[#f08019] opacity-90" />
                </div>
                {t(5).image_url && <img src={t(5).image_url} alt="" className="relative max-h-28 max-w-[70%] w-auto object-contain z-10" />}
              </div>
              <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4" style={{ color: t(5).text_color }}>
                <span className="text-2xl font-black tracking-wider">{t(5).title}</span>
                <p className="mt-2 text-sm opacity-90">{t(5).subtitle}</p>
              </div>
            </Link>
          </div>
        )}

        {/* Slot 6-7 */}
        <div className="grid grid-cols-1 gap-5 mt-5">
          <HorizontalBanner tile={t(6)} height="h-[160px]" />
          <HorizontalBanner tile={t(7)} height="h-[160px]" />
        </div>

        {/* Slot 8 */}
        <div className="mt-5">
          <HorizontalBanner tile={t(8)} height="h-[160px]" />
        </div>

        {/* Slot 9-11 */}
        <div className="grid md:grid-cols-3 gap-5 mt-5">
          <VerticalCard tile={t(9)} />
          <VerticalCard tile={t(10)} />
          <VerticalCard tile={t(11)} />
        </div>

        {/* Slot 12-14 */}
        <div className="grid md:grid-cols-3 gap-5 mt-5">
          <VerticalCard tile={t(12)} />
          <VerticalCard tile={t(13)} />
          <VerticalCard tile={t(14)} />
        </div>
      </section>
    </div>
  );
};

export default Home;
